import { useState, useEffect } from "react";
import { fetchProductsByCna } from "../api";
import { LEVEL_COLOR } from "../utils/prevalence";
import { fmtTs } from "../utils/time";

// Sorted by CNA name to start. The table exists to be read down a column —
// "who assigns the ubiquitous software" — so every header sorts, but the
// default is the grouping key rather than a ranking the reader didn't ask for.
const DEFAULT_SORT = { key: "cna", dir: "asc" };

function sortRows(rows, sort) {
  const { key, dir } = sort;
  const sign = dir === "asc" ? 1 : -1;
  const value = (row) => {
    if (key === "cna") return row.cna.toLowerCase();
    if (key === "products" || key === "cves" || key === "unresolved") return row[key];
    return row.levels[key] || 0;
  };
  return [...rows].sort((a, b) => {
    const av = value(a), bv = value(b);
    if (av < bv) return -1 * sign;
    if (av > bv) return 1 * sign;
    // Ties on a count column are common — a stable second key by name keeps
    // the row order from shuffling between renders.
    return a.cna.toLowerCase() < b.cna.toLowerCase() ? -1 : 1;
  });
}

function SortHeader({ label, sortKey, sort, setSort, color, title }) {
  const active = sort.key === sortKey;
  return (
    <th
      onClick={() => setSort(active
        ? { key: sortKey, dir: sort.dir === "asc" ? "desc" : "asc" }
        // Counts are most useful largest-first; names read A→Z.
        : { key: sortKey, dir: sortKey === "cna" ? "asc" : "desc" })}
      style={{
        cursor: "pointer", whiteSpace: "nowrap",
        color: active ? "var(--accent)" : color,
        textAlign: sortKey === "cna" ? "left" : "right",
      }}
      title={title}
    >
      {label}{active ? (sort.dir === "asc" ? " ▲" : " ▼") : ""}
    </th>
  );
}

function Count({ n, color }) {
  // A zero in a dense grid is noise — the eye should land on the cells that
  // have something in them.
  if (!n) return <span style={{ color: "var(--text-muted)", opacity: 0.4 }}>·</span>;
  return <span style={{ color }}>{n.toLocaleString()}</span>;
}

export default function ProductsByCnaPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    fetchProductsByCna()
      .then(setData)
      .catch((err) => setError(err.message || "Could not load per-CNA counts"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading">Loading per-CNA counts...</div>;
  if (error) return <div className="jobs-empty" style={{ color: "#f85149" }}>{error}</div>;
  if (!data) return null;

  const levels = data.levels || [];
  const totals = data.totals || {};
  const q = filter.trim().toLowerCase();
  const rows = sortRows(
    (data.cnas || []).filter((r) => !q || r.cna.toLowerCase().includes(q)),
    sort,
  );

  const numCell = { textAlign: "right", fontVariantNumeric: "tabular-nums" };

  return (
    <div className="panel">
      <div className="panel-header">
        Prevalence by CNA
        <span className="products-summary">
          {(totals.cnas || 0).toLocaleString()} CNAs &middot;{" "}
          {(totals.products || 0).toLocaleString()} distinct products &middot;{" "}
          {(totals.cves || 0).toLocaleString()} analysed CVEs
        </span>
      </div>

      <form className="products-toolbar" onSubmit={(e) => e.preventDefault()}>
        <input
          className="products-search-input"
          type="text"
          placeholder="Filter CNAs..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        {filter && (
          <button type="button" className="flush-btn" onClick={() => setFilter("")}>Clear</button>
        )}
      </form>

      {rows.length === 0 ? (
        <div className="jobs-empty">No CNAs match this filter.</div>
      ) : (
        <table className="products-table">
          <thead>
            <tr>
              <SortHeader label="CNA" sortKey="cna" sort={sort} setSort={setSort} />
              <SortHeader
                label="Products" sortKey="products" sort={sort} setSort={setSort}
                title="Distinct products named by this CNA's analysed CVEs"
              />
              {levels.map((level) => (
                <SortHeader
                  key={level} label={level} sortKey={level} sort={sort} setSort={setSort}
                  color={LEVEL_COLOR[level]}
                  title={level === "unassessed"
                    ? "Resolved to a product, but nobody has assessed it yet"
                    : `Products assessed as ${level}`}
                />
              ))}
              <SortHeader
                label="CVEs" sortKey="cves" sort={sort} setSort={setSort}
                title="Analysed CVEs from this CNA that carry a prevalence snapshot"
              />
              <SortHeader
                label="Unresolved" sortKey="unresolved" sort={sort} setSort={setSort}
                title="affected[] entries that matched no product in the master list. Mentions, not distinct products — so these are outside the level columns."
              />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.cna} className="products-row">
                <td className="products-vendor">{row.cna}</td>
                <td style={{ ...numCell, fontWeight: 600 }}>{row.products.toLocaleString()}</td>
                {levels.map((level) => (
                  <td key={level} style={numCell}>
                    <Count n={row.levels[level]} color={LEVEL_COLOR[level]} />
                  </td>
                ))}
                <td style={{ ...numCell, color: "var(--text-secondary)" }}>{row.cves.toLocaleString()}</td>
                <td style={numCell}>
                  <Count n={row.unresolved} color="var(--text-secondary)" />
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            {/* Distinct across every CNA, not the sum of the column: a product
                named by two CNAs is a real product for each of them, so the
                rows above deliberately double-count it and this row does not. */}
            <tr style={{ borderTop: "2px solid var(--border)", fontWeight: 600 }}>
              <td className="products-vendor" title="Distinct across all CNAs — less than the column sum, since two CNAs can name the same product">
                All CNAs (distinct)
              </td>
              <td style={numCell}>{(totals.products || 0).toLocaleString()}</td>
              {levels.map((level) => (
                <td key={level} style={{ ...numCell, color: LEVEL_COLOR[level] }}>
                  {((totals.levels || {})[level] || 0).toLocaleString()}
                </td>
              ))}
              <td style={numCell}>{(totals.cves || 0).toLocaleString()}</td>
              <td style={numCell}>&nbsp;</td>
            </tr>
          </tfoot>
        </table>
      )}

      <div style={{ padding: "8px 12px", fontSize: 11, color: "var(--text-muted)" }}>
        A product counts for a CNA when one of that CNA's analysed CVEs named it, matched
        to the products master list. Levels come from that master list, so they are current
        rather than as-of each CVE's snapshot.
        {data.computed_at && <> Computed {fmtTs(data.computed_at)}.</>}
      </div>
    </div>
  );
}
